import {
  abbreviatedCCSQuestionsIds,
  CCSPrimaryQuestionIds,
  CCSSecondaryQuestionIds,
  ReevalSurveyQuestionIds,
} from '@chiroup/core/constants/CCSQuestionIds';
import { SectionTitles } from '@chiroup/core/enums/SectionTitles.enum';
import dayjs from 'dayjs';
import {
  ComplaintSummary,
  ComplaintSummaryType,
  IfCondition,
  QuestionAnswer,
} from '../types/ComplaintSummary.type';
import type { UnifiedQAEntry } from './buildUnifiedQuestionAnswerMap';

type QuestionAnswerMap = Record<string, QuestionAnswer>;

export interface FlexibleDisplayItem {
  type: 'text' | 'nested_section' | 'yellow_flags';
  content?: string;
  template?: string;
  questionId?: string;
  questionData?: QuestionAnswerMap;
  nestedTitle?: string;
  nestedItems?: FlexibleDisplayItem[];
  yellowFlags?: string[];
}

export interface FlexibleDisplaySection {
  title: string;
  items: FlexibleDisplayItem[];
}

/**
 * Converts a single flexible CCS complaint summary to the same format as buildCCSResponse output
 * @param summary Single complaint summary to process
 * @param createdAt Optional timestamp for when the response was created
 * @returns FlexibleDisplayResponse matching buildCCSResponse format
//  */

/**
 * Question IDs that need Yes/No to descriptive text conversion
 */
const questionIdsThatNeedYesNoConverted: Record<string, boolean> = {
  // Seatbelt questions
  [CCSPrimaryQuestionIds.seatbeltQuestionId]: true,
  [CCSSecondaryQuestionIds.seatbeltQuestionId]: true,

  // Airbag questions
  [CCSPrimaryQuestionIds.airbagQuestionId]: true,
  [CCSSecondaryQuestionIds.airbagQuestionId]: true,

  // Brake questions
  [CCSPrimaryQuestionIds.onBrakeQuestionId]: true,
  [CCSSecondaryQuestionIds.onBrakeQuestionId]: true,

  // Vehicle drivable questions
  [CCSPrimaryQuestionIds.vehicleDrivableQuestionId]: true,
  [CCSSecondaryQuestionIds.vehicleDrivableQuestionId]: true,

  // Attorney questions
  [CCSPrimaryQuestionIds.attorneyInjuryQuestionId]: true,
  [CCSSecondaryQuestionIds.attorneyInjuryQuestionId]: true,

  // Same as above questions
  // Note: Show raw Yes/No for previous similar symptoms questions
  // [CCSPrimaryQuestionIds.sameSymptomsId]: true,
  // [CCSSecondaryQuestionIds.sameIncidentQuestionId]: true,

  // Were you questions (specific options)
  [CCSPrimaryQuestionIds.autoWereYouQuestionId]: true,
  [CCSSecondaryQuestionIds.autoWereYouQuestionId]: true,
};

/**
 * Convert Yes/No answers to descriptive text based on question context
 */
export function convertYesNoToDescriptive(
  questionId: string,
  value: string,
  questionText: string
): string {
  // Handle specific question IDs for auto accident status
  if (
    questionId === CCSPrimaryQuestionIds.seatbeltQuestionId ||
    questionId === CCSSecondaryQuestionIds.seatbeltQuestionId ||
    questionText?.toLowerCase().includes('seatbelt')
  ) {
    return value === 'Yes' ? 'wearing seatbelt' : 'not wearing seatbelt';
  }

  if (
    questionId === CCSPrimaryQuestionIds.airbagQuestionId ||
    questionId === CCSSecondaryQuestionIds.airbagQuestionId ||
    questionText?.toLowerCase().includes('airbag')
  ) {
    return value === 'Yes' ? 'air bag deployed' : 'no air bag deployed';
  }

  if (
    questionId === CCSPrimaryQuestionIds.onBrakeQuestionId ||
    questionId === CCSSecondaryQuestionIds.onBrakeQuestionId ||
    questionText?.toLowerCase().includes('brake')
  ) {
    return value === 'Yes' ? 'foot on brake' : 'foot not on brake';
  }

  if (
    questionId === CCSPrimaryQuestionIds.vehicleDrivableQuestionId ||
    questionId === CCSSecondaryQuestionIds.vehicleDrivableQuestionId ||
    questionText?.toLowerCase().includes('driveable')
  ) {
    return value === 'Yes' ? 'drivable' : 'not drivable';
  }

  if (
    questionId === CCSPrimaryQuestionIds.attorneyInjuryQuestionId ||
    questionId === CCSSecondaryQuestionIds.attorneyInjuryQuestionId ||
    questionText?.toLowerCase().includes('attorney')
  ) {
    return value === 'Yes' ? 'has attorney' : 'no attorney';
  }

  if (
    questionId === CCSPrimaryQuestionIds.sameSymptomsId ||
    questionText?.toLowerCase().includes('same as above')
  ) {
    return value === 'Yes' ? 'same as problem 1' : 'different from problem 1';
  }

  if (
    questionId === CCSPrimaryQuestionIds.autoWereYouQuestionId ||
    questionId === CCSSecondaryQuestionIds.autoWereYouQuestionId ||
    questionText?.toLowerCase().includes('were you')
  ) {
    // This handles the "Were you?" question which has specific options
    return value; // Return the actual selected value (e.g., "Braced for impact", "Unaware of the impending collision")
  }

  // Default fallback for other Yes/No questions
  return value;
}

export const checkIfCondition = (
  ifCondition: IfCondition,
  questionAnswerMap: QuestionAnswerMap
): boolean => {
  if (!ifCondition || !ifCondition.id) {
    return true; // No condition means always show
  }

  const questionData = questionAnswerMap[ifCondition.id];

  if (!questionData) {
    return false; // Question not answered, don't show conditional content
  }

  // If condition value is null, it means "any value present"
  if (ifCondition.value === null) {
    const hasValues = questionData.selectedValues?.length > 0;

    return hasValues;
  }

  // If condition value is an array, check if any of the selected values match any of the condition values
  if (Array.isArray(ifCondition.value)) {
    const matches = questionData.selectedValues?.some((selectedValue: string) =>
      (ifCondition.value as (string | number | boolean)[]).includes(selectedValue)
    );

    return matches;
  }

  // Check if any of the selected values match the condition value (single value)
  const matches = questionData.selectedValues?.some(
    (value: string) => value === (ifCondition.value as string | number | boolean)
  );

  return matches;
};

/**
 * The renderer resolves `{subId}` placeholders against top-level keys, but group
 * sub-answers live nested on the group entry. Flatten them into a copy for display;
 * the stored map is left untouched so section builders don't emit duplicate lines.
 */
export const withGroupSubAnswers = (
  map?: Record<string, QuestionAnswer> | null
): Record<string, QuestionAnswer> => {
  if (!map) return {};
  const out: Record<string, QuestionAnswer> = { ...map };

  for (const entry of Object.values(map)) {
    if (entry?.questionType === 'group' && Array.isArray(entry?.groupSubAnswers)) {
      for (const sub of entry.groupSubAnswers) {
        if (sub?.id) out[sub.id] = sub;
      }
    }
  }

  return out;
};

/**
 * Find the id of the group entry that owns a sub-question, so an inline edit can be
 * routed into its nested `groupSubAnswers` array (see withGroupSubAnswers) rather than
 * orphaned as a stale top-level key. Returns undefined for a normal top-level question.
 */
export const findGroupParentId = (
  map: Record<string, QuestionAnswer> | null | undefined,
  subId: string
): string | undefined => {
  if (!map || !subId) return undefined;

  for (const [id, entry] of Object.entries(map)) {
    if (
      entry?.questionType === 'group' &&
      Array.isArray(entry?.groupSubAnswers) &&
      entry.groupSubAnswers.some(sub => sub?.id === subId)
    ) {
      return id;
    }
  }

  return undefined;
};

type GroupSubWriteSummary = {
  complaintRelatedQuestions?: Record<string, QuestionAnswer> | null;
};

/**
 * Write an edited group sub-answer into the parent group's `groupSubAnswers` for the
 * active complaint, and — when `shared` is true — into every other complaint that
 * carries the same group, keeping shared groups in sync like top-level shared questions.
 * Returns a new summaries map; pass a clone you own.
 */
export const writeGroupSubAnswer = <T extends GroupSubWriteSummary>(
  summaries: Record<string, T>,
  activeComplaintId: string,
  parentGroupId: string,
  subId: string,
  updatedSub: QuestionAnswer,
  shared: boolean
): Record<string, T> => {
  const next: Record<string, T> = { ...summaries };

  const updateOne = (cid: string) => {
    const summary = next[cid];
    const crq = summary?.complaintRelatedQuestions;
    const group = crq?.[parentGroupId];

    if (
      !group ||
      !Array.isArray(group.groupSubAnswers) ||
      !group.groupSubAnswers.some(sub => sub?.id === subId)
    ) {
      return;
    }

    next[cid] = {
      ...summary,
      complaintRelatedQuestions: {
        ...crq,
        [parentGroupId]: {
          ...group,
          groupSubAnswers: group.groupSubAnswers.map(sub => (sub?.id === subId ? updatedSub : sub)),
        },
      },
    };
  };

  updateOne(activeComplaintId);

  if (shared) {
    for (const cid of Object.keys(next)) {
      if (cid && cid !== activeComplaintId) updateOne(cid);
    }
  }

  return next;
};

type RenderedSurveySummary = {
  patientSurveyId?: string;
  complaintRelatedQuestions?: Record<string, unknown> | null;
};

/**
 * Collect the ids of surveys the subjective generator already rendered inline, so
 * callers don't append them a second time. Only counts *active* complaints with
 * non-empty complaintRelatedQuestions — a survey tied to a skipped complaint, or one
 * that rendered nothing inline, is still appended rather than dropped.
 */
export const collectRenderedSurveyIds = (
  complaintSummaries: Record<string, RenderedSurveySummary> | undefined,
  activeComplaintsProblemAreas: Record<string, boolean> | undefined
): Set<string> => {
  const ids = new Set<string>();

  for (const [complaintId, summary] of Object.entries(complaintSummaries || {})) {
    if (
      activeComplaintsProblemAreas?.[complaintId] &&
      summary?.patientSurveyId &&
      summary?.complaintRelatedQuestions &&
      Object.keys(summary.complaintRelatedQuestions).length > 0
    ) {
      ids.add(summary.patientSurveyId);
    }
  }

  return ids;
};

export const buildFlexibleSections = (summary: ComplaintSummary): FlexibleDisplaySection[] => {
  // Build CCS/Interim/Reeval-style sections from complaintRelatedQuestions using canonical templates
  const unifiedMap = (
    summary as unknown as {
      complaintRelatedQuestions?: Record<string, UnifiedQAEntry>;
    }
  )?.complaintRelatedQuestions;

  const summaryType = (summary as unknown as { type?: ComplaintSummaryType })?.type;
  const behavesAs = (
    summary as unknown as {
      behavesAs?: 'ccs' | 'abbreviatedCCS' | 'reevaluation' | 'interim' | null;
    }
  )?.behavesAs;

  // Dayjs will handle date formatting consistently; no manual month names needed

  const toQA = (id: string): QuestionAnswer | undefined => {
    const e = unifiedMap?.[id];
    if (!e) return undefined;

    return entryToQA(e, id);
  };

  // Resolve a single UnifiedQAEntry into a QuestionAnswer. Split out from toQA so
  // group sub-answers (which live nested on the group entry, not in unifiedMap)
  // can be resolved with the same logic.
  const entryToQA = (e: UnifiedQAEntry, id?: string): QuestionAnswer => {
    const options = (e.options || []).map(opt => ({ id: opt.id, display: opt.display }));
    const baseSelected = [...(e.selectedValues || [])];
    const selectedValues =
      e.questionType === 'date'
        ? baseSelected.map(v => {
            const s = String(v);
            let d = dayjs(s);

            if (!d.isValid()) {
              // Attempt to parse common MM/DD/YYYY as fallback
              const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

              if (m) {
                d = dayjs(`${m[3]}-${m[1]}-${m[2]}`);
              }
            }

            if (!d.isValid()) {
              const asDate = new Date(s);
              if (!isNaN(asDate.getTime())) d = dayjs(asDate);
            }

            return d.isValid() ? d.format('MM/DD/YYYY') : s;
          })
        : baseSelected;

    const qa: QuestionAnswer = {
      questionText: e.questionText,
      questionType: e.questionType as QuestionAnswer['questionType'],
      selectedValues,
      options,
      ...(e.otherValue ? { otherValue: e.otherValue } : {}),
      ...(e.customInputData ? { customInputData: e.customInputData } : {}),
    } as QuestionAnswer;

    // Force specific types to mirror staging
    if (id === Q.aggravatesQuestionId) {
      qa.questionType = 'combobox';
    }

    if (id === Q.providerQuestionId) {
      qa.questionType = 'checkboxWithInputs';
    }

    return qa;
  };

  // Render a group question as a labeled sub-section: the group's label as a
  // header, then one line per sub-question. Unanswered subs render too (as the
  // interactive "None" placeholder) so a provider can add an answer inline —
  // matching how top-level questions always show. Returns null only when the
  // group defines no sub-questions. Shared by every builder that lists questions
  // so the group handling can't diverge between them.
  const buildGroupNestedSection = (entry: UnifiedQAEntry): FlexibleDisplayItem | null => {
    const nestedItems: FlexibleDisplayItem[] = [];

    for (const sub of entry.groupSubAnswers || []) {
      if (!sub?.id) continue;
      const subQA = entryToQA(sub);
      const subText = (sub.questionText || '').replace(/:\s*$/, '');
      const subTemplate = subText ? `${subText}: {${sub.id}}` : `{${sub.id}}`;

      nestedItems.push({
        type: 'text',
        template: subTemplate,
        questionId: sub.id,
        questionData: { [sub.id]: subQA },
      });
    }

    if (!nestedItems.length) return null;

    return {
      type: 'nested_section',
      nestedTitle: (entry.questionText || '').replace(/:\s*$/, '') || 'Group',
      nestedItems,
    };
  };

  const findKeyByIncludes = (fragment: string): string | undefined => {
    if (!fragment) return undefined;
    const keys = Object.keys(unifiedMap || {});

    return keys.find(k => k.includes(fragment));
  };

  const getSelectedValues = (id: string): string[] => {
    const entry = unifiedMap?.[id];

    return Array.isArray(entry?.selectedValues) ? (entry!.selectedValues as string[]) : [];
  };

  const meetsIf = (cond?: { id?: string; value?: unknown }): boolean => {
    if (!cond || !cond.id) return true;
    const selected = getSelectedValues(cond.id);

    if (cond.value === null || cond.value === 'null') {
      return selected.length > 0;
    }

    if (Array.isArray(cond.value)) {
      return selected.some(v => (cond.value as (string | number | boolean)[]).includes(v));
    }

    return selected.includes(cond.value as string);
  };

  const conditionsMetForId = (id?: string): boolean => {
    if (!id) return false;
    const entry = unifiedMap?.[id] as UnifiedQAEntry | undefined;
    if (!entry) return false;
    // All 'if' must be true
    const ifOk = Array.isArray(entry.if)
      ? (entry.if as Array<{ id?: string; value?: unknown }>).every(c => meetsIf(c))
      : true;
    // Any 'ifSome' may be true (if present). If none present, treat as true
    const ifSomeOk = Array.isArray(entry.ifSome)
      ? (entry.ifSome as Array<{ id?: string; value?: unknown }>).some(c => meetsIf(c))
      : true;

    return ifOk && ifSomeOk;
  };

  const hasAnswer = (id?: string): boolean => {
    if (!id) return false;
    const vals = getSelectedValues(id);

    return vals.length > 0;
  };

  const _pushIfAnswered = (items: FlexibleDisplayItem[], id: string, template: string) => {
    if (conditionsMetForId(id) && hasAnswer(id)) {
      items.push({ type: 'text', template, questionData: buildQAForTemplate(template) });
    }
  };

  const pushIfVisible = (items: FlexibleDisplayItem[], id: string, template: string) => {
    if (conditionsMetForId(id)) {
      items.push({ type: 'text', template, questionData: buildQAForTemplate(template) });
    }
  };

  const _pushIfPresent = (items: FlexibleDisplayItem[], id: string, template: string) => {
    if (id && unifiedMap?.[id]) {
      items.push({ type: 'text', template, questionData: buildQAForTemplate(template) });
    }
  };

  const computeTimingDescription = (percent: number, which: 'days' | 'eachDay'): string => {
    if (which === 'days') {
      return percent < 20
        ? 'rarely'
        : percent < 40
          ? 'moderately'
          : percent < 60
            ? 'frequently'
            : 'continually';
    }

    return percent < 20
      ? 'infrequently'
      : percent < 40
        ? 'moderately'
        : percent < 60
          ? 'frequently'
          : 'continually';
  };

  const buildQAForTemplate = (template: string): Record<string, QuestionAnswer> => {
    const qam: Record<string, QuestionAnswer> = {};
    const ids = extractQuestionIds(template);
    ids.forEach(id => {
      // Handle _description placeholders for timing
      if (id.endsWith('_description')) {
        const baseId = id.replace('_description', '');
        const qa = toQA(baseId);

        if (qa && qa.questionType === 'slider' && qa.selectedValues?.length) {
          const valueNum = Number(qa.selectedValues[0] || 0);
          const which =
            baseId === CCSPrimaryQuestionIds.percentOfDaysQuestionId ||
            baseId === CCSSecondaryQuestionIds.percentOfDaysQuestionId
              ? 'days'
              : 'eachDay';
          qam[id] = {
            questionText: '',
            questionType: qa.questionType,
            selectedValues: [computeTimingDescription(valueNum, which as 'days' | 'eachDay')],
            options: [],
          } as QuestionAnswer;
        }
      } else {
        const qa = toQA(id);
        if (qa) qam[id] = qa;
      }
    });

    return qam;
  };

  const useSecondary = !!unifiedMap?.[CCSSecondaryQuestionIds.mainQuestionId];
  const Q = useSecondary
    ? (CCSSecondaryQuestionIds as Record<string, string>)
    : (CCSPrimaryQuestionIds as Record<string, string>);

  // Local question-id mapping to mirror buildFlexibleCCSResponse's QUESTION_ID_MAPPINGS
  const M = useSecondary
    ? {
        mainQuestionId: CCSSecondaryQuestionIds.mainQuestionId,
        onsetQuestionId: CCSSecondaryQuestionIds.symptomsBeginQuestionId,
        whenQuestionId: CCSSecondaryQuestionIds.presentEpisodeQuestionId,
        dueToQuestionId: CCSSecondaryQuestionIds.causedQuestionId,
        brieflyDescribeQuestionId: CCSSecondaryQuestionIds.brieflyDescribeQuestionId,
        previousSimilarSymptomsQuestionId: CCSSecondaryQuestionIds.sameSymptomsId,
        frequencyDescriptionQuestionId: CCSSecondaryQuestionIds.estimateQuestionId,
        symptomProgressionQuestionId: CCSSecondaryQuestionIds.onsetQuestionId,
        percentOfDaysQuestionId: CCSSecondaryQuestionIds.percentOfDaysQuestionId,
        percentOfEachDayQuestionId: CCSSecondaryQuestionIds.symptomsArePresentQuestionId,
        // Auto accident details
        accidentTypeQuestionId: CCSSecondaryQuestionIds.descriptionOfAccidentQuestionId,
        dateOfAccidentQuestionId: CCSSecondaryQuestionIds.dateOfAccidentQuestionId,
        secondaryCollisionsQuestionId: CCSSecondaryQuestionIds.secondaryCollisionsQuestionId,
        patientVehicleTypeQuestionId: CCSSecondaryQuestionIds.vehicleTypeQuestionId,
        patientVehicleDamageQuestionId: CCSSecondaryQuestionIds.vehicleDamageQuestionId,
        patientVehicleDriveableQuestionId: CCSSecondaryQuestionIds.vehicleDrivableQuestionId,
        patientVehicleDirectionQuestionId: CCSSecondaryQuestionIds.vehicleSittingQuestionId,
        speedQuestionId: CCSSecondaryQuestionIds.speedEstimateQuestionId,
        otherVehicleTypeQuestionId: CCSSecondaryQuestionIds.otherVehicleTypeQuestionId,
        otherSpeedEstimateQuestionRadioId:
          CCSSecondaryQuestionIds.otherSpeedEstimateQuestionRadioId,
        roadConditionsQuestionId: CCSSecondaryQuestionIds.roadConditionsQuestionId,
        lookingQuestionId: CCSSecondaryQuestionIds.impactLookingQuestionId,
        brakeQuestionId: CCSSecondaryQuestionIds.onBrakeQuestionId,
        awarenessQuestionId: CCSSecondaryQuestionIds.autoWereYouQuestionId,
        seatbeltQuestionId: CCSSecondaryQuestionIds.seatbeltQuestionId,
        airbagQuestionId: CCSSecondaryQuestionIds.airbagQuestionId,
        noticeSymptomsQuestionId: CCSSecondaryQuestionIds.noticeSymptomsQuestionId,
        seekCareQuestionId: CCSSecondaryQuestionIds.seekCareQuestionId,
        attorneyInjuryQuestionId: CCSSecondaryQuestionIds.attorneyInjuryQuestionId,
        attorneyNameInjuryQuestionId: CCSSecondaryQuestionIds.attorneyNameInjuryQuestionId,
        // Work injury details
        workInjuryDescriptionQuestionId: CCSSecondaryQuestionIds.descriptionInjuryQuestionId,
        workInjuryDateQuestionId: CCSSecondaryQuestionIds.whenDidInjuryQuestionId,
        workInjuryLocationQuestionId: CCSSecondaryQuestionIds.whereDidInjuryQuestionId,
        reportedToQuestionId: CCSSecondaryQuestionIds.whoReportInjuryQuestionId,
        didYouReportInjuryQuestionId: CCSSecondaryQuestionIds.didYouReportInjuryQuestionId,
        lostWorkInjuryQuestionId: CCSSecondaryQuestionIds.lostWorkInjuryQuestionId,
        frequencyLostWorkQuestionId: CCSSecondaryQuestionIds.howOftenLostWorkInjuryQuestionId,
        currentlyOffWorkQuestionId: CCSSecondaryQuestionIds.currentlyOffWorkInjuryQuestionId,
        employerQuestionId: CCSSecondaryQuestionIds.employerQuestionId,
        occupationQuestionId: CCSSecondaryQuestionIds.occupationQuestionId,
        contactPersonQuestionId: CCSSecondaryQuestionIds.contactPersonQuestionId,
        employerContactQuestionId: CCSSecondaryQuestionIds.contactPersonInfoQuestionId,
      }
    : {
        mainQuestionId: CCSPrimaryQuestionIds.mainQuestionId,
        onsetQuestionId: CCSPrimaryQuestionIds.symptomsBeginQuestionId,
        whenQuestionId: CCSPrimaryQuestionIds.presentEpisodeQuestionId,
        dueToQuestionId: CCSPrimaryQuestionIds.causedQuestionId,
        brieflyDescribeQuestionId: CCSPrimaryQuestionIds.brieflyDescribeQuestionId,
        previousSimilarSymptomsQuestionId: CCSPrimaryQuestionIds.sameSymptomsId,
        frequencyDescriptionQuestionId: CCSPrimaryQuestionIds.estimateQuestionId,
        symptomProgressionQuestionId: CCSPrimaryQuestionIds.onsetQuestionId,
        percentOfDaysQuestionId: CCSPrimaryQuestionIds.percentOfDaysQuestionId,
        percentOfEachDayQuestionId: CCSPrimaryQuestionIds.symptomsArePresentQuestionId,
        // Auto accident details
        accidentTypeQuestionId: CCSPrimaryQuestionIds.descriptionOfAccidentQuestionId,
        dateOfAccidentQuestionId: CCSPrimaryQuestionIds.dateOfAccidentQuestionId,
        secondaryCollisionsQuestionId: CCSPrimaryQuestionIds.secondaryCollisionsQuestionId,
        patientVehicleTypeQuestionId: CCSPrimaryQuestionIds.vehicleTypeQuestionId,
        patientVehicleDamageQuestionId: CCSPrimaryQuestionIds.vehicleDamageQuestionId,
        patientVehicleDriveableQuestionId: CCSPrimaryQuestionIds.vehicleDrivableQuestionId,
        patientVehicleDirectionQuestionId: CCSPrimaryQuestionIds.vehicleSittingQuestionId,
        speedQuestionId: CCSPrimaryQuestionIds.speedEstimateQuestionId,
        otherVehicleTypeQuestionId: CCSPrimaryQuestionIds.otherVehicleTypeQuestionId,
        otherSpeedEstimateQuestionRadioId: CCSPrimaryQuestionIds.otherSpeedEstimateQuestionRadioId,
        roadConditionsQuestionId: CCSPrimaryQuestionIds.roadConditionsQuestionId,
        lookingQuestionId: CCSPrimaryQuestionIds.impactLookingQuestionId,
        brakeQuestionId: CCSPrimaryQuestionIds.onBrakeQuestionId,
        awarenessQuestionId: CCSPrimaryQuestionIds.autoWereYouQuestionId,
        seatbeltQuestionId: CCSPrimaryQuestionIds.seatbeltQuestionId,
        airbagQuestionId: CCSPrimaryQuestionIds.airbagQuestionId,
        noticeSymptomsQuestionId: CCSPrimaryQuestionIds.noticeSymptomsQuestionId,
        seekCareQuestionId: CCSPrimaryQuestionIds.seekCareQuestionId,
        attorneyInjuryQuestionId: CCSPrimaryQuestionIds.attorneyInjuryQuestionId,
        attorneyNameInjuryQuestionId: CCSPrimaryQuestionIds.attorneyNameInjuryQuestionId,
        // Work injury details
        workInjuryDescriptionQuestionId: CCSPrimaryQuestionIds.descriptionInjuryQuestionId,
        workInjuryDateQuestionId: CCSPrimaryQuestionIds.whenDidInjuryQuestionId,
        workInjuryLocationQuestionId: CCSPrimaryQuestionIds.whereDidInjuryQuestionId,
        reportedToQuestionId: CCSPrimaryQuestionIds.whoReportInjuryQuestionId,
        didYouReportInjuryQuestionId: CCSPrimaryQuestionIds.didYouReportInjuryQuestionId,
        lostWorkInjuryQuestionId: CCSPrimaryQuestionIds.lostWorkInjuryQuestionId,
        frequencyLostWorkQuestionId: CCSPrimaryQuestionIds.howOftenLostWorkInjuryQuestionId,
        currentlyOffWorkQuestionId: CCSPrimaryQuestionIds.currentlyOffWorkInjuryQuestionId,
        employerQuestionId: CCSPrimaryQuestionIds.employerQuestionId,
        occupationQuestionId: CCSPrimaryQuestionIds.occupationQuestionId,
        contactPersonQuestionId: CCSPrimaryQuestionIds.contactPersonQuestionId,
        employerContactQuestionId: CCSPrimaryQuestionIds.contactPersonInfoQuestionId,
      };

  // ----- Builders for non-CCS types (Interim / Reeval) using unified map -----
  const buildInterimFromUnified = (): FlexibleDisplaySection[] => {
    const interimSections: FlexibleDisplaySection[] = [];

    // Overview section - consolidates status, symptom specifics, and exercise items
    // Custom interim surveys may use different question ID prefixes than the built-in,
    // so we try the built-in ID first, then fall back to the custom template ID.
    const statusId = findKeyByIncludes('interimSymptomsGeneralStatus');
    const vasId = findKeyByIncludes('interimSymptomsGeneralVAS');
    const provocativeId =
      findKeyByIncludes('interimSymptomsSpecificProvocative') ||
      findKeyByIncludes('interimAggravates');
    const palliativeId =
      findKeyByIncludes('interimSymptomsSpecificPalliative') || findKeyByIncludes('interimRelief');
    const flexibilityId = findKeyByIncludes('interimSymptomsSpecificFlexibility');
    const painPercentId =
      findKeyByIncludes('interimSymptomsSpecificPainPercent') ||
      findKeyByIncludes('interimPainPercent');
    const presentPercentId =
      findKeyByIncludes('interimSymptomsSpecificPresentPercent') ||
      findKeyByIncludes('interimPresentPercent');
    const adlFreqId = findKeyByIncludes('interimExerciseADLFrequency');
    const adlImpId = findKeyByIncludes('interimExerciseADLImpediments');
    const adlStartId = findKeyByIncludes('interimExerciseADLStartedADL');

    const overviewItems: FlexibleDisplayItem[] = [];

    // Status items
    if (statusId) {
      const t = `Status: {${statusId}}`;
      overviewItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    if (vasId) {
      const t = `VAS: {${vasId}}`;
      overviewItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    // Symptom specifics items
    if (provocativeId) {
      const t = `ADL improvements: {${provocativeId}}`;
      overviewItems.push({
        type: 'text',
        template: t,
        questionData: buildQAForTemplate(t),
      });
    }

    if (palliativeId) {
      const t = `Decreased utilization: {${palliativeId}}`;
      overviewItems.push({
        type: 'text',
        template: t,
        questionData: buildQAForTemplate(t),
      });
    }

    if (flexibilityId) {
      const t = `Flexibility: {${flexibilityId}}`;
      overviewItems.push({
        type: 'text',
        template: t,
        questionData: buildQAForTemplate(t),
      });
    }

    if (painPercentId) {
      const t = `Pain frequency: {${painPercentId}}`;
      overviewItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    if (presentPercentId) {
      const t = `Pain presence: {${presentPercentId}}`;
      overviewItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    // Exercise items
    if (adlFreqId) {
      const t = `Exercise performance: {${adlFreqId}}`;
      overviewItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    if (adlImpId) {
      const t = `Exercise impediments: {${adlImpId}}`;
      overviewItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    if (adlStartId) {
      const t = `ADL implementation: {${adlStartId}}`;
      overviewItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    if (overviewItems.length) {
      interimSections.push({ title: 'Overview', items: overviewItems });
    }

    // General questions section - only new complaint items
    // Map key order isn't guaranteed: skip the details textbox, whose key also
    // contains this id, or the row shows the details instead of Yes/No.
    const newSymptomsId = Object.keys(unifiedMap || {}).find(
      k => k.includes('interimSymptomsGeneralNewSymptoms') && !k.includes('TextBox')
    );
    const newSymptomsTextId = findKeyByIncludes('interimSymptomsGeneralNewSymptomsTextBox');

    const generalItems: FlexibleDisplayItem[] = [];

    if (newSymptomsId) {
      const t = `New complaint: {${newSymptomsId}}`;
      generalItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    // Only show details textbox when its condition is satisfied (i.e., New complaint === Yes)
    if (newSymptomsTextId && conditionsMetForId(newSymptomsTextId)) {
      const t = `New complaint details: {${newSymptomsTextId}}`;
      generalItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    if (generalItems.length) {
      interimSections.push({ title: SectionTitles.GeneralQuestions, items: generalItems });
    }

    return interimSections;
  };

  const buildReevalFromUnified = (): FlexibleDisplaySection[] => {
    const reevalSections: FlexibleDisplaySection[] = [];

    // Overview
    const overviewItems: FlexibleDisplayItem[] = [];
    const statusId = findKeyByIncludes(ReevalSurveyQuestionIds.reevalSymptomsQuestionId);

    if (statusId) {
      const t = `Status: {${statusId}}`;
      overviewItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    const improvementId = findKeyByIncludes(
      ReevalSurveyQuestionIds.reevalSymptomsPercentImprovedQuestionId
    );

    if (improvementId && conditionsMetForId(improvementId)) {
      const t = `Patient reported improvement: {${improvementId}}%`;
      overviewItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    const spreadId = findKeyByIncludes(ReevalSurveyQuestionIds.reevalsymptomsSpreadQuestionId);

    if (spreadId) {
      const t = `Symptom Distribution: {${spreadId}}`;
      overviewItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    const providerId =
      findKeyByIncludes(CCSPrimaryQuestionIds.providerQuestionId) ||
      findKeyByIncludes(CCSSecondaryQuestionIds.providerQuestionId);

    if (providerId) {
      const providerQA = toQA(providerId);
      const hasProviders = providerQA?.selectedValues && providerQA.selectedValues.length > 0;

      if (hasProviders) {
        const t = `New provider consultations: Since the last evaluation, the patient reports consulting {${providerId}}`;
        overviewItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
      } else {
        // Show "denies consulting" when no providers selected - make "denies" clickable
        const qd = buildQAForTemplate(`{${providerId}}`);

        overviewItems.push({
          type: 'text',
          template: `New provider consultations: Since the last evaluation, the patient {${providerId}} consulting other practitioners.`,
          questionData: qd,
          questionId: providerId,
        });
      }
    }

    if (overviewItems.length) {
      reevalSections.push({ title: 'Overview', items: overviewItems });
    }

    // Provocative / Palliative (single-line templates)
    const provId =
      findKeyByIncludes(CCSPrimaryQuestionIds.aggravatesQuestionId) ||
      findKeyByIncludes(CCSSecondaryQuestionIds.aggravatesQuestionId);

    if (provId) {
      const t = `{${provId}}`;
      reevalSections.push({
        title: SectionTitles.ProvocativeFactors,
        items: [{ type: 'text', template: t, questionData: buildQAForTemplate(t) }],
      });
    }

    const pallId =
      findKeyByIncludes(CCSPrimaryQuestionIds.reliefQuestionId) ||
      findKeyByIncludes(CCSSecondaryQuestionIds.reliefQuestionId);

    if (pallId) {
      const t = `{${pallId}}`;
      reevalSections.push({
        title: SectionTitles.PalliativeFactors,
        items: [{ type: 'text', template: t, questionData: buildQAForTemplate(t) }],
      });
    }

    // Quality - only show categories selected in main; if selected but no descriptors, show just the label
    const qualityItems: FlexibleDisplayItem[] = [];
    const mainId =
      findKeyByIncludes(CCSPrimaryQuestionIds.mainQuestionId) ||
      findKeyByIncludes(CCSSecondaryQuestionIds.mainQuestionId);
    const mainQA = mainId ? toQA(mainId) : undefined;
    const selectedMain = (mainQA?.selectedValues as string[]) || [];
    const isSelected = (label: string) => selectedMain.includes(label);

    const pushQuality = (label: string, descId?: string) => {
      if (!isSelected(label)) return;

      const descIdResolved =
        descId ||
        (label === 'Pain / Discomfort'
          ? findKeyByIncludes(CCSPrimaryQuestionIds.bestDescribesQuestionPainId) ||
            findKeyByIncludes(CCSSecondaryQuestionIds.bestDescribesQuestionPainId)
          : label === 'Altered sensation'
            ? findKeyByIncludes(CCSPrimaryQuestionIds.bestDescribesQuestionAlteredId) ||
              findKeyByIncludes(CCSSecondaryQuestionIds.bestDescribesQuestionAlteredId)
            : label === 'Weakness'
              ? findKeyByIncludes(CCSPrimaryQuestionIds.bestDescribesQuestionWeaknessId) ||
                findKeyByIncludes(CCSSecondaryQuestionIds.bestDescribesQuestionWeaknessId)
              : undefined);

      if (descIdResolved) {
        const descQA = toQA(descIdResolved);
        const hasDesc = !!descQA?.selectedValues?.length;

        if (hasDesc) {
          const t = `<span data-question-id="${mainId}" data-symptom-value="${label}" class="clickable-symptom">${label}</span> described as {${descIdResolved}}`;
          const qd = buildQAForTemplate(`{${descIdResolved}}`);
          // Add main question data so the popover can display all symptom options
          if (mainQA) qd[mainId!] = mainQA;
          qualityItems.push({
            type: 'text',
            template: t,
            questionData: qd,
          });
        } else {
          // For descriptor categories selected with no descriptor answers, show "described as None"
          const t = `<span data-question-id="${mainId}" data-symptom-value="${label}" class="clickable-symptom">${label}</span> described as {${descIdResolved}}`;
          const qd: Record<string, QuestionAnswer> = {};
          qd[descIdResolved] = {
            questionText: descQA?.questionText || '',
            questionType: (descQA?.questionType as QuestionAnswer['questionType']) || 'checkbox',
            selectedValues: ['None'],
            options: [],
          } as QuestionAnswer;
          // Add main question data so the popover can display all symptom options
          if (mainQA) qd[mainId!] = mainQA;
          qualityItems.push({ type: 'text', template: t, questionData: qd });
        }
      } else {
        const t = `<span data-question-id="${mainId}" data-symptom-value="${label}" class="clickable-symptom">${label}</span>`;
        const qd: Record<string, QuestionAnswer> = {};
        // Add main question data so the popover can display all symptom options
        if (mainQA) qd[mainId!] = mainQA;
        qualityItems.push({ type: 'text', template: t, questionData: qd });
      }
    };

    // Described categories
    pushQuality('Pain / Discomfort');
    pushQuality('Altered sensation');
    pushQuality('Weakness');
    // Binary categories
    pushQuality('Noise / Grinding');
    pushQuality('Stiffness / Tightness');

    // Other (only when explicitly selected in main and otherValue present)
    if (isSelected('other') && (mainQA as QuestionAnswer | undefined)?.otherValue) {
      const otherLabel = String((mainQA as QuestionAnswer).otherValue);
      const t = `<span data-question-id="${mainId}" data-symptom-value="${otherLabel}" class="clickable-symptom">${otherLabel}</span>`;
      qualityItems.push({ type: 'text', template: t, questionData: {} });
    }

    if (qualityItems.length === 0 && mainId) {
      const mainQuestionAnswerMap = mainQA ? { [mainId]: mainQA } : {};
      qualityItems.push({
        type: 'text',
        template: `<span data-question-id="${mainId}" data-symptom-value="None" class="clickable-symptom">[None]</span>`,
        questionId: mainId,
        questionData: mainQuestionAnswerMap,
      });
    }

    if (qualityItems.length > 0 || mainId) {
      reevalSections.push({ title: SectionTitles.Quality, items: qualityItems });
    }

    // Severity (VAS, life enjoyment, general activity)
    const sevItems: FlexibleDisplayItem[] = [];

    // VAS questions are conditional on "Pain / Discomfort" being selected in main question
    const avgIdCandidate =
      findKeyByIncludes(CCSPrimaryQuestionIds.intensityQuestionId) ||
      findKeyByIncludes(CCSSecondaryQuestionIds.intensityQuestionId) ||
      (unifiedMap?.[abbreviatedCCSQuestionsIds.averageSeverityQuestionId]
        ? abbreviatedCCSQuestionsIds.averageSeverityQuestionId
        : undefined);

    const avgShown =
      avgIdCandidate &&
      toQA(avgIdCandidate) &&
      (avgIdCandidate === abbreviatedCCSQuestionsIds.averageSeverityQuestionId ||
        conditionsMetForId(avgIdCandidate));

    if (avgShown) {
      const t = `Pain severity: VAS average {${avgIdCandidate}}/10`;
      sevItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    const highId =
      findKeyByIncludes(CCSPrimaryQuestionIds.intensity2QuestionId) ||
      findKeyByIncludes(CCSSecondaryQuestionIds.intensity2QuestionId);

    if (highId && toQA(highId) && conditionsMetForId(highId)) {
      const t = `Pain severity: VAS high {${highId}}/10`;
      sevItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    const lifeId =
      findKeyByIncludes(CCSPrimaryQuestionIds.interferedQuestionId) ||
      findKeyByIncludes(CCSSecondaryQuestionIds.interferedQuestionId);

    if (lifeId && toQA(lifeId)) {
      const t = `Life enjoyment interference: {${lifeId}}/10`;
      sevItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    const gaId =
      findKeyByIncludes(CCSPrimaryQuestionIds.generalInterferedQuestionId) ||
      findKeyByIncludes(CCSSecondaryQuestionIds.generalInterferedQuestionId);

    if (gaId && toQA(gaId)) {
      const t = `General activity interference: {${gaId}}/10`;
      sevItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    if (sevItems.length) {
      reevalSections.push({ title: SectionTitles.Severity, items: sevItems });
    }

    // Timing same as CCS using percent ids
    const pdKey =
      findKeyByIncludes(CCSPrimaryQuestionIds.percentOfDaysQuestionId) ||
      findKeyByIncludes(CCSSecondaryQuestionIds.percentOfDaysQuestionId);
    const peKey =
      findKeyByIncludes(CCSPrimaryQuestionIds.symptomsArePresentQuestionId) ||
      findKeyByIncludes(CCSSecondaryQuestionIds.symptomsArePresentQuestionId);

    if (pdKey || peKey) {
      const parts: string[] = [];
      if (pdKey) parts.push(`Symptoms present {${pdKey}_description} ({${pdKey}}% of days)`);
      if (peKey) parts.push(`{${peKey}_description} each day ({${peKey}}% of the day)`);
      const t = parts.join(', ');
      reevalSections.push({
        title: SectionTitles.Timing,
        items: [{ type: 'text', template: t, questionData: buildQAForTemplate(t) }],
      });
    }

    return reevalSections;
  };

  // ----- Builder for Custom CCS using Q&A format -----
  const buildCustomCCSFromUnified = (): FlexibleDisplaySection[] => {
    if (!unifiedMap) return [];

    const items: FlexibleDisplayItem[] = [];

    for (const [questionId, entry] of Object.entries(unifiedMap)) {
      if (!entry) continue;
      if (entry.questionType === 'bodypicker') continue;

      // Respect conditional visibility (if/ifSome)
      if (!conditionsMetForId(questionId)) continue;

      // Group questions render as a labeled sub-section.
      if (entry.questionType === 'group') {
        const groupSection = buildGroupNestedSection(entry);
        if (groupSection) items.push(groupSection);
        continue;
      }

      const qa = toQA(questionId);
      if (!qa) continue;

      // Each question: question text as static prefix, answer as interactive placeholder
      const rawText = qa.questionText || entry.questionText || '';
      const questionText = rawText.replace(/:\s*$/, '');
      const template = questionText ? `${questionText}: {${questionId}}` : `{${questionId}}`;

      items.push({
        type: 'text',
        template,
        questionId,
        questionData: { [questionId]: qa },
      });
    }

    if (!items.length) return [];

    return [
      {
        title: 'Survey Responses',
        items,
      },
    ];
  };

  // ----- Builder for Initial/CCS using unified map -----
  const buildInitialFromUnified = (): FlexibleDisplaySection[] => {
    const flexSections: FlexibleDisplaySection[] = [];

    // Onset (modeled after generateCCSSectionConfig)
    const onsetItems: FlexibleDisplayItem[] = [];

    const dueToSelected = getSelectedValues(M.dueToQuestionId)[0];

    const resolveWhenQuestionId = (): string => {
      if (hasAnswer(M.whenQuestionId)) {
        return M.whenQuestionId;
      }

      if (
        dueToSelected === 'Auto accident' &&
        M.dateOfAccidentQuestionId &&
        hasAnswer(M.dateOfAccidentQuestionId)
      ) {
        return M.dateOfAccidentQuestionId;
      }

      if (
        dueToSelected === 'Employment-related injury' &&
        M.workInjuryDateQuestionId &&
        hasAnswer(M.workInjuryDateQuestionId)
      ) {
        return M.workInjuryDateQuestionId;
      }

      return M.whenQuestionId;
    };

    const whenQuestionId = resolveWhenQuestionId();

    if (toQA(M.onsetQuestionId) || toQA(whenQuestionId) || toQA(M.dueToQuestionId)) {
      const whenQA = toQA(whenQuestionId);
      const whenHasAgo = !!whenQA?.selectedValues?.[0]?.toString()?.toLowerCase()?.includes('ago');
      const defaultOnset = `Beginning {${M.onsetQuestionId}} ${whenHasAgo ? 'approximately ' : ''}${
        whenHasAgo ? '' : 'on '
      }{${whenQuestionId}} due to {${M.dueToQuestionId}}`;
      onsetItems.push({
        type: 'text',
        template: defaultOnset,
        questionData: buildQAForTemplate(defaultOnset),
      });

      // Cause description shown only for Single or Repetitive activity
      const causeDescT = `Cause description: {${M.brieflyDescribeQuestionId}}`;
      onsetItems.push({
        type: 'text',
        template: causeDescT,
        questionData: buildQAForTemplate(causeDescT),
      });

      // Previous similar symptoms
      const prevSimilarT = `Previous similar symptoms: {${M.previousSimilarSymptomsQuestionId}}`;
      onsetItems.push({
        type: 'text',
        template: prevSimilarT,
        questionData: buildQAForTemplate(prevSimilarT),
      });

      const similarBeganT = `Similar symptoms began: {${M.frequencyDescriptionQuestionId}}`;
      onsetItems.push({
        type: 'text',
        template: similarBeganT,
        questionData: buildQAForTemplate(similarBeganT),
      });

      // Symptom progression
      const progressionT = `Symptoms {${M.symptomProgressionQuestionId}}`;
      onsetItems.push({
        type: 'text',
        template: progressionT,
        questionData: buildQAForTemplate(progressionT),
      });
    }

    if (onsetItems.length) {
      // Cause description conditional
      const hasCause = getSelectedValues(M.dueToQuestionId).some(v =>
        ['Single activity', 'Repetitive activity'].includes(v)
      );
      const filtered = onsetItems.filter(item => {
        if (item.template?.startsWith('Cause description')) {
          return hasCause;
        }

        if (item.template?.startsWith('Similar symptoms began')) {
          return getSelectedValues(M.previousSimilarSymptomsQuestionId).includes('Yes');
        }

        return true;
      });

      if (filtered.length) {
        flexSections.push({ title: SectionTitles.Onset, items: filtered });
      }
    }

    if (dueToSelected === 'Auto accident') {
      const autoItems: FlexibleDisplayItem[] = [];
      pushIfVisible(
        autoItems,
        M.accidentTypeQuestionId,
        `Accident type: {${M.accidentTypeQuestionId}}`
      );
      pushIfVisible(
        autoItems,
        M.secondaryCollisionsQuestionId,
        `Secondary collision: {${M.secondaryCollisionsQuestionId}}`
      );
      // Patient vehicle information grouped together
      const pDir = conditionsMetForId(M.patientVehicleDirectionQuestionId);
      const pType = conditionsMetForId(M.patientVehicleTypeQuestionId);
      const pSpeed = conditionsMetForId(M.speedQuestionId);

      if (pDir || pType || pSpeed) {
        const parts: string[] = [];
        if (pDir) parts.push(`Patient vehicle: {${M.patientVehicleDirectionQuestionId}}`);
        if (pType) parts.push(`in a {${M.patientVehicleTypeQuestionId}}`);
        if (pSpeed) parts.push(`traveling at {${M.speedQuestionId}} mph`);
        const t = parts.join(', ').replace('Patient vehicle:', 'Patient vehicle:');
        autoItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
      }

      pushIfVisible(
        autoItems,
        M.patientVehicleDamageQuestionId,
        `Patient vehicle damage: {${M.patientVehicleDamageQuestionId}}`
      );
      pushIfVisible(
        autoItems,
        M.patientVehicleDriveableQuestionId,
        `Vehicle drivable: {${M.patientVehicleDriveableQuestionId}}`
      );

      // Other vehicle information grouped together
      pushIfVisible(
        autoItems,
        M.otherVehicleTypeQuestionId,
        `Other vehicle: {${M.otherVehicleTypeQuestionId}}`
      );
      pushIfVisible(
        autoItems,
        M.otherSpeedEstimateQuestionRadioId,
        `Other vehicle speed: {${M.otherSpeedEstimateQuestionRadioId}}`
      );
      pushIfVisible(
        autoItems,
        M.roadConditionsQuestionId,
        `Road conditions: {${M.roadConditionsQuestionId}}`
      );
      // Status at impact composite
      const look = conditionsMetForId(M.lookingQuestionId);
      const aware = conditionsMetForId(M.awarenessQuestionId);
      const belt = conditionsMetForId(M.seatbeltQuestionId);
      const airbag = conditionsMetForId(M.airbagQuestionId);

      if (look || aware || belt || airbag) {
        const segs: string[] = [];
        if (look) segs.push(`looking {${M.lookingQuestionId}}`);
        if (aware) segs.push(`{${M.awarenessQuestionId}}`);
        if (belt) segs.push(`{${M.seatbeltQuestionId}}`);
        if (airbag) segs.push(`{${M.airbagQuestionId}}`);
        const t = `Status at time of impact: ${segs.join(', ')}`;
        autoItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
      }

      if (M.noticeSymptomsQuestionId) {
        pushIfVisible(
          autoItems,
          M.noticeSymptomsQuestionId,
          `Symptoms noticed: {${M.noticeSymptomsQuestionId}}`
        );
      }

      if (M.seekCareQuestionId) {
        pushIfVisible(
          autoItems,
          M.seekCareQuestionId,
          `First sought care: {${M.seekCareQuestionId}}`
        );
      }

      if (M.attorneyInjuryQuestionId) {
        pushIfVisible(
          autoItems,
          M.attorneyInjuryQuestionId,
          `Attorney: {${M.attorneyInjuryQuestionId}}`
        );
      }

      if (M.attorneyNameInjuryQuestionId && conditionsMetForId(M.attorneyNameInjuryQuestionId)) {
        const t = `Attorney name/phone: {${M.attorneyNameInjuryQuestionId}}`;
        autoItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
      }

      if (autoItems.length) {
        flexSections.push({ title: SectionTitles.AutoAccidentDetails, items: autoItems });
      }
    } else if (dueToSelected === 'Employment-related injury') {
      const workItems: FlexibleDisplayItem[] = [];
      pushIfVisible(workItems, M.employerQuestionId, `Employer: {${M.employerQuestionId}}`);
      pushIfVisible(workItems, M.occupationQuestionId, `Occupation: {${M.occupationQuestionId}}`);
      pushIfVisible(
        workItems,
        M.workInjuryDescriptionQuestionId,
        `Work injury description: {${M.workInjuryDescriptionQuestionId}}`
      );
      pushIfVisible(
        workItems,
        M.workInjuryLocationQuestionId,
        `Location: {${M.workInjuryLocationQuestionId}}`
      );
      // The 'Did you report' (didYouReportInjuryQuestionId) gates 'Reported to' details (whoReportInjuryQuestionId)
      pushIfVisible(
        workItems,
        M.didYouReportInjuryQuestionId,
        `Did you report: {${M.didYouReportInjuryQuestionId}}`
      );
      pushIfVisible(workItems, M.reportedToQuestionId, `Reported to: {${M.reportedToQuestionId}}`);
      pushIfVisible(
        workItems,
        M.lostWorkInjuryQuestionId,
        `Lost work: {${M.lostWorkInjuryQuestionId}}`
      );
      pushIfVisible(
        workItems,
        M.frequencyLostWorkQuestionId,
        `Lost work frequency: {${M.frequencyLostWorkQuestionId}}`
      );
      pushIfVisible(
        workItems,
        M.currentlyOffWorkQuestionId,
        `Currently off work: {${M.currentlyOffWorkQuestionId}}`
      );
      pushIfVisible(
        workItems,
        M.contactPersonQuestionId,
        `Employer contact person: {${M.contactPersonQuestionId}}`
      );
      pushIfVisible(
        workItems,
        M.employerContactQuestionId,
        `Employer contact: {${M.employerContactQuestionId}}`
      );

      if (M.attorneyInjuryQuestionId) {
        pushIfVisible(
          workItems,
          M.attorneyInjuryQuestionId,
          `Attorney: {${M.attorneyInjuryQuestionId}}`
        );
      }

      if (M.attorneyNameInjuryQuestionId && conditionsMetForId(M.attorneyNameInjuryQuestionId)) {
        const t = `Attorney name/phone: {${M.attorneyNameInjuryQuestionId}}`;
        workItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
      }

      if (workItems.length) {
        flexSections.push({ title: SectionTitles.WorkInjuryDetails, items: workItems });
      }
    }

    // Provocative factors
    if (toQA(Q.aggravatesQuestionId)) {
      const t = `{${Q.aggravatesQuestionId}}`;
      flexSections.push({
        title: SectionTitles.ProvocativeFactors,
        items: [{ type: 'text', template: t, questionData: buildQAForTemplate(t) }],
      });
    }

    // Palliative factors
    if (toQA(Q.reliefQuestionId)) {
      const t = `{${Q.reliefQuestionId}}`;
      flexSections.push({
        title: SectionTitles.PalliativeFactors,
        items: [{ type: 'text', template: t, questionData: buildQAForTemplate(t) }],
      });
    }

    // Quality - only show categories selected in main; if selected but no descriptors, show just the label
    const qualityItems: FlexibleDisplayItem[] = [];
    const mainId = Q.mainQuestionId;
    const mainQA = toQA(mainId);
    const selectedMain = (mainQA?.selectedValues as string[]) || [];
    const isSelected = (label: string) => selectedMain.includes(label);

    const pushQuality = (label: string, descId?: string) => {
      if (!isSelected(label)) return;

      if (descId) {
        const descQA = toQA(descId);
        const hasDesc = !!descQA?.selectedValues?.length;

        if (hasDesc) {
          const t = `<span data-question-id="${mainId}" data-symptom-value="${label}" class="clickable-symptom">${label}</span> described as {${descId}}`;
          const qd = buildQAForTemplate(`{${descId}}`);
          // Add main question data so the popover can display all symptom options
          if (mainQA) qd[mainId] = mainQA;
          qualityItems.push({
            type: 'text',
            template: t,
            questionData: qd,
          });
        } else {
          // For descriptor categories selected with no descriptor answers, show "described as None"
          const t = `<span data-question-id="${mainId}" data-symptom-value="${label}" class="clickable-symptom">${label}</span> described as {${descId}}`;
          const qd: Record<string, QuestionAnswer> = {};
          qd[descId] = {
            questionText: descQA?.questionText || '',
            questionType: (descQA?.questionType as QuestionAnswer['questionType']) || 'checkbox',
            selectedValues: ['None'],
            options: [],
          } as QuestionAnswer;
          // Add main question data so the popover can display all symptom options
          if (mainQA) qd[mainId] = mainQA;
          qualityItems.push({ type: 'text', template: t, questionData: qd });
        }
      } else {
        const t = `<span data-question-id="${mainId}" data-symptom-value="${label}" class="clickable-symptom">${label}</span>`;
        const qd: Record<string, QuestionAnswer> = {};
        // Add main question data so the popover can display all symptom options
        if (mainQA) qd[mainId] = mainQA;
        qualityItems.push({ type: 'text', template: t, questionData: qd });
      }
    };

    // Described categories
    pushQuality('Pain / Discomfort', Q.bestDescribesQuestionPainId);
    pushQuality('Altered sensation', Q.bestDescribesQuestionAlteredId);
    pushQuality('Weakness', Q.bestDescribesQuestionWeaknessId);
    // Binary categories
    pushQuality('Noise / Grinding');
    pushQuality('Stiffness / Tightness');

    // Other (only when explicitly selected in main and otherValue present)
    if (isSelected('other') && (mainQA as QuestionAnswer | undefined)?.otherValue) {
      const otherLabel = String((mainQA as QuestionAnswer).otherValue);
      const t = `<span data-question-id="${mainId}" data-symptom-value="${otherLabel.replace(/"/g, '')}" class="clickable-symptom">${otherLabel}</span>`;
      qualityItems.push({ type: 'text', template: t, questionData: {} });
    }

    // If no quality symptoms were selected, add [None] fallback
    if (selectedMain.length === 0 && mainId && mainQA) {
      qualityItems.push({
        type: 'text',
        template: `<span data-question-id="${mainId}" data-symptom-value="None" class="clickable-symptom">[None]</span>`,
        questionId: mainId,
        questionData: { [mainId]: mainQA },
      });
    }

    // Abbreviated survey specific questions - only show if they exist
    // "Since the last visit, are your symptoms:" question (abbreviated CCS only)
    const sinceLastVisitId = abbreviatedCCSQuestionsIds.sinceLastVisitQuestionId;

    if (sinceLastVisitId && hasAnswer(sinceLastVisitId)) {
      const t = `Since last visit symptoms are: {${sinceLastVisitId}}`;
      qualityItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    // "Average symptom severity" question (abbreviated survey specific)
    // const avgSeverityId = Object.keys(unifiedMap || {}).find(
    //   id =>
    //     unifiedMap?.[id]?.questionText?.toLowerCase().includes('average') &&
    //     unifiedMap?.[id]?.questionText?.toLowerCase().includes('symptom severity') &&
    //     unifiedMap?.[id]?.questionType === 'slider'
    // );

    // if (avgSeverityId && hasAnswer(avgSeverityId)) {
    //   const t = `Average symptom severity rated: {${avgSeverityId}}/10`;
    //   qualityItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    // }

    flexSections.push({ title: SectionTitles.Quality, items: qualityItems });

    // Severity
    const severityItems: FlexibleDisplayItem[] = [];

    // VAS questions are conditional on "Pain / Discomfort" being selected in main question
    const averageSeverityId =
      (toQA(Q.intensityQuestionId) && conditionsMetForId(Q.intensityQuestionId)
        ? Q.intensityQuestionId
        : undefined) ||
      (toQA(abbreviatedCCSQuestionsIds.averageSeverityQuestionId)
        ? abbreviatedCCSQuestionsIds.averageSeverityQuestionId
        : undefined);

    if (averageSeverityId) {
      severityItems.push({
        type: 'text',
        template: `Pain severity: VAS average {${averageSeverityId}}/10`,
        questionData: buildQAForTemplate(`{${averageSeverityId}}`),
      });
    }

    if (toQA(Q.intensity2QuestionId) && conditionsMetForId(Q.intensity2QuestionId)) {
      severityItems.push({
        type: 'text',
        template: `Pain severity: VAS high {${Q.intensity2QuestionId}}/10`,
        questionData: buildQAForTemplate(`{${Q.intensity2QuestionId}}`),
      });
    }

    if (toQA(Q.interferedQuestionId)) {
      severityItems.push({
        type: 'text',
        template: `Life enjoyment interference: {${Q.interferedQuestionId}}/10`,
        questionData: buildQAForTemplate(`{${Q.interferedQuestionId}}`),
      });
    }

    if (toQA(Q.generalInterferedQuestionId)) {
      severityItems.push({
        type: 'text',
        template: `General activity interference: {${Q.generalInterferedQuestionId}}/10`,
        questionData: buildQAForTemplate(`{${Q.generalInterferedQuestionId}}`),
      });
    }

    if (severityItems.length) {
      flexSections.push({ title: SectionTitles.Severity, items: severityItems });
    }

    // Timing
    const timingItems: FlexibleDisplayItem[] = [];

    if (toQA(M.percentOfDaysQuestionId) || toQA(M.percentOfEachDayQuestionId)) {
      const t = `Symptoms present {${M.percentOfDaysQuestionId}_description} ({${M.percentOfDaysQuestionId}}% of days), {${M.percentOfEachDayQuestionId}_description} each day ({${M.percentOfEachDayQuestionId}}% of the day)`;
      timingItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    if (timingItems.length) {
      flexSections.push({ title: SectionTitles.Timing, items: timingItems });
    }

    // Additional Information
    const additionalItems: FlexibleDisplayItem[] = [];

    if (toQA(Q.seekCareQuestionId)) {
      const t = `Sought care: {${Q.seekCareQuestionId}}`;
      additionalItems.push({
        type: 'text',
        template: t,
        questionData: buildQAForTemplate(t),
      });
    }

    if (toQA(Q.providerQuestionId)) {
      const t = `Providers seen: {${Q.providerQuestionId}}`;
      additionalItems.push({
        type: 'text',
        template: t,
        questionData: buildQAForTemplate(t),
      });
    }

    if (toQA(CCSPrimaryQuestionIds.pregnantQuestionId)) {
      const t = `Pregnancy status: {${CCSPrimaryQuestionIds.pregnantQuestionId}}`;
      additionalItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    // Show pregnancy status always, but only show due date / LMP when their conditions are met
    if (conditionsMetForId(CCSPrimaryQuestionIds.pregnantConfirmedDateQuestionId)) {
      const t = `Due date: {${CCSPrimaryQuestionIds.pregnantConfirmedDateQuestionId}}`;
      additionalItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    if (conditionsMetForId(CCSPrimaryQuestionIds.pregnantSuspectedDateQuestionId)) {
      const t = `Date of last menstrual period (approximate): {${CCSPrimaryQuestionIds.pregnantSuspectedDateQuestionId}}`;
      additionalItems.push({ type: 'text', template: t, questionData: buildQAForTemplate(t) });
    }

    if (additionalItems.length) {
      flexSections.push({ title: SectionTitles.AdditionalInformation, items: additionalItems });
    }

    // Yellow Flags (shared)
    if (toQA(CCSPrimaryQuestionIds.yellowFlagsQuestionId)) {
      const t = `Yellow flags: {${CCSPrimaryQuestionIds.yellowFlagsQuestionId}}`;
      const qaMap = buildQAForTemplate(t);
      const yfQA = qaMap[CCSPrimaryQuestionIds.yellowFlagsQuestionId];
      const yfValues = Array.isArray(yfQA?.selectedValues)
        ? yfQA.selectedValues.map((v: string) =>
            v === 'other' && yfQA.otherValue ? yfQA.otherValue : v
          )
        : [];
      flexSections.push({
        title: SectionTitles.YellowFlags,
        items: [
          {
            type: 'text',
            template: t,
            questionData: qaMap,
            yellowFlags: yfValues,
          },
        ],
      });
    }

    return flexSections;
  };

  // Append custom (non-standard) questions present in the unifiedMap but not
  // already rendered by the type-specific builder. Mutates and returns `sections`.
  const appendCustomQuestions = (
    sections: FlexibleDisplaySection[],
    skipQuestionId?: (id: string) => boolean
  ): FlexibleDisplaySection[] => {
    // Collect all question IDs referenced in the existing sections' templates
    const consumedIds = new Set<string>();

    for (const section of sections) {
      for (const item of section.items) {
        if (item.template) {
          for (const id of extractQuestionIds(item.template)) {
            consumedIds.add(id);

            // Also mark _description variants as consumed
            if (id.endsWith('_description')) {
              consumedIds.add(id.replace('_description', ''));
            }
          }
        }

        // Also mark IDs referenced in data-question-id attributes
        const spanMatches = item.template?.matchAll(/data-question-id="([^"]+)"/g);

        if (spanMatches) {
          for (const m of spanMatches) {
            consumedIds.add(m[1]);
          }
        }
      }
    }

    // Find questions in unifiedMap that weren't consumed
    if (unifiedMap) {
      const extraItems: FlexibleDisplayItem[] = [];

      // DynamoDB maps don't preserve key order — sort by stamped survey
      // position (legacy rows without `order` keep map order, last).
      const orderedEntries = Object.entries(unifiedMap).sort(
        ([, a], [, b]) =>
          (a?.order ?? Number.MAX_SAFE_INTEGER) - (b?.order ?? Number.MAX_SAFE_INTEGER)
      );

      for (const [questionId, entry] of orderedEntries) {
        if (!entry || consumedIds.has(questionId)) continue;
        if (skipQuestionId?.(questionId)) continue;
        if (entry.questionType === 'bodypicker') continue;

        // Respect conditional visibility (if/ifSome)
        if (!conditionsMetForId(questionId)) continue;

        // Group questions render as a labeled sub-section.
        if (entry.questionType === 'group') {
          const groupSection = buildGroupNestedSection(entry);
          if (groupSection) extraItems.push(groupSection);
          continue;
        }

        const qa = toQA(questionId);
        if (!qa) continue;

        const rawText = qa.questionText || entry.questionText || '';
        const questionText = rawText.replace(/:\s*$/, '');
        const template = questionText ? `${questionText}: {${questionId}}` : `{${questionId}}`;

        extraItems.push({
          type: 'text',
          template,
          questionId,
          questionData: { [questionId]: qa },
        });
      }

      if (extraItems.length) {
        sections.push({
          title: 'Survey Responses',
          items: extraItems,
        });
      }
    }

    return sections;
  };

  if (summaryType === ComplaintSummaryType.Interim) {
    // Built-in interim ids all start with "interim" — rendered above or
    // deliberately omitted (demographics). Custom question ids are UUIDs.
    return appendCustomQuestions(buildInterimFromUnified(), id => id.startsWith('interim'));
  }

  if (summaryType === ComplaintSummaryType.Reeval) {
    return buildReevalFromUnified();
  }

  // For custom CCS with no standard CCS questions, use pure Q&A format
  if (behavesAs === 'ccs' || behavesAs === 'abbreviatedCCS') {
    // Check if ANY standard CCS question IDs exist in the unifiedMap
    const hasStandardCCSQuestions = Object.keys(Q).some(key => !!unifiedMap?.[Q[key]]);

    if (!hasStandardCCSQuestions) {
      return buildCustomCCSFromUnified();
    }
  }

  // Build standard CCS sections, then append any unprocessed custom questions
  return appendCustomQuestions(buildInitialFromUnified());
};

export interface TemplatePart {
  type: 'text' | 'question' | 'clickable-symptom';
  content: string;
  questionId?: string;
  questionData?: any;
  symptomValue?: string;
}

/**
 * Extract question IDs from a template string
 */
// fallow-ignore-next-line unused-export
export function extractQuestionIds(template: string): string[] {
  const placeholderRegex = /\{([^}]+)\}/g;
  const questionIds: string[] = [];
  let match;

  while ((match = placeholderRegex.exec(template)) !== null) {
    questionIds.push(match[1]);
  }

  return questionIds;
}

/**
 * Parse template with support for clickable symptom spans
 */
export function parseTemplateWithClickableSymptoms(
  template: string,
  questionAnswerMap?: QuestionAnswerMap
): TemplatePart[] {
  const parts: TemplatePart[] = [];
  const placeholderRegex = /\{([^}]+)\}/g;
  const clickableSymptomRegex =
    /<span data-question-id="([^"]+)" data-symptom-value="([^"]+)" class="clickable-symptom">([^<]+)<\/span>/g;
  const clickableValueRegex =
    /<span data-question-id="([^"]+)" class="clickable-value">([^<]+)<\/span>/g;

  let lastIndex = 0;
  let placeholderMatch;
  let clickableMatch;
  let clickableValueMatch;

  // First, find all clickable symptom spans
  const clickableMatches: Array<{
    index: number;
    match: RegExpExecArray;
    type: 'symptom' | 'value';
  }> = [];

  while ((clickableMatch = clickableSymptomRegex.exec(template)) !== null) {
    clickableMatches.push({ index: clickableMatch.index, match: clickableMatch, type: 'symptom' });
  }

  // Find all clickable value spans
  while ((clickableValueMatch = clickableValueRegex.exec(template)) !== null) {
    clickableMatches.push({
      index: clickableValueMatch.index,
      match: clickableValueMatch,
      type: 'value',
    });
  }

  // Then find all placeholder matches
  const placeholderMatches: Array<{ index: number; match: RegExpExecArray }> = [];

  while ((placeholderMatch = placeholderRegex.exec(template)) !== null) {
    placeholderMatches.push({ index: placeholderMatch.index, match: placeholderMatch });
  }

  // Combine and sort all matches by index
  const allMatches = [
    ...clickableMatches.map(m => ({ ...m, isClickable: true })),
    ...placeholderMatches.map(m => ({ ...m, isClickable: false })),
  ].sort((a, b) => a.index - b.index);

  for (const item of allMatches) {
    const { index, match, isClickable } = item;

    // Add text before the match
    if (index > lastIndex) {
      const textContent = template.slice(lastIndex, index);

      if (textContent.trim()) {
        parts.push({
          type: 'text',
          content: textContent,
        });
      }
    }

    if (isClickable) {
      const clickableItem = item as {
        index: number;
        match: RegExpExecArray;
        type: 'symptom' | 'value';
        isClickable: true;
      };

      if (clickableItem.type === 'symptom') {
        // Handle clickable symptom span
        const questionId = match[1];
        const symptomValue = match[2];
        const displayText = match[3];
        const questionData = questionAnswerMap?.[questionId];

        parts.push({
          type: 'clickable-symptom',
          content: displayText,
          questionId,
          questionData,
          symptomValue,
        });
      } else {
        // Handle clickable value span
        const questionId = match[1];
        const displayText = match[2];
        const questionData = questionAnswerMap?.[questionId];

        parts.push({
          type: 'question',
          content: displayText,
          questionId,
          questionData,
        });
      }
    } else {
      // Handle regular question placeholder
      const questionId = match[1];
      const questionData = questionAnswerMap?.[questionId];

      parts.push({
        type: 'question',
        content: match[0],
        questionId,
        questionData,
      });
    }

    lastIndex = index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < template.length) {
    const textContent = template.slice(lastIndex);

    if (textContent.trim()) {
      parts.push({
        type: 'text',
        content: textContent,
      });
    }
  }

  return parts;
}

/**
 * Get display value for a question without HTML formatting
 */
export function getQuestionDisplayValue(
  questionId: string,
  questionData: QuestionAnswer
): string | string[] {
  // Handle the special case where value is 0 (falsy but valid)
  if (questionData.selectedValues?.length === 1 && questionData.selectedValues[0] === '0') {
    return '0';
  }

  // Check if we have no selected values
  if (!questionData.selectedValues?.length) {
    return '[No response]';
  }

  // Check if this question needs Yes/No conversion
  if (
    questionData.questionType === 'radio' &&
    (questionData.selectedValues[0] === 'Yes' || questionData.selectedValues[0] === 'No') &&
    questionIdsThatNeedYesNoConverted[questionId]
  ) {
    const descriptiveValue = convertYesNoToDescriptive(
      questionId,
      questionData.selectedValues[0],
      questionData.questionText
    );

    return descriptiveValue;
  }

  if (questionData.questionType === 'date') {
    const dateValue = questionData.selectedValues[0];

    if (!dateValue) return '[No response]';

    // Strings with relative phrasing (e.g., "3 days ago") should be shown as-is
    if (dateValue.toLowerCase().includes('ago')) {
      return dateValue;
    }

    // Handle MM/DD/YYYY explicitly, else let dayjs parse
    const mmddyyyy = dateValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

    if (mmddyyyy) {
      return dayjs(`${mmddyyyy[3]}-${mmddyyyy[1]}-${mmddyyyy[2]}`).format('MM/DD/YYYY');
    }

    const parsed = dayjs(dateValue);

    return parsed.isValid() ? parsed.format('MM/DD/YYYY') : dateValue;
  }

  // Handle "other" option by replacing with otherValue, and map id→display for options
  const optionsMap = questionData.options?.length
    ? new Map(questionData.options.map(o => [o.id, o.display]))
    : undefined;

  const displayValues = questionData.selectedValues.map((value: string) => {
    if (value === 'other' && questionData.otherValue) {
      return questionData.otherValue;
    }

    // Use option display text when it differs from the id
    const optDisplay = optionsMap?.get(value);

    if (optDisplay && optDisplay !== value) {
      return optDisplay;
    }

    return value;
  });

  // Handle scale questions (add /10 suffix)
  if (questionData.questionType === 'slider') {
    return `${displayValues[0]}`;
  }

  // For textarea/input questions, preserve newlines
  if (questionData.questionType === 'textarea' || questionData.questionType === 'input') {
    return displayValues[0] || '';
  }

  // For checkbox questions, return comma-separated string
  if (questionData.questionType === 'checkbox') {
    return displayValues.join(', ');
  }

  return displayValues.join(', ');
}
